// Dauer-Tracking (2026-06-12) — reaktiviert die seit dem Vike-Cutover
// (31.05.) tote Verweildauer in `scrolly_engagements.duration`.
//
// Der alte app/-Viewer schickte periodisch maxProgress+duration an
// `/scrollys/:pk/engagement` (authentifiziert). Der public/-Reader (anonym,
// seit Cutover live) tat das nie — nur useViewTracking schickt den 25%-
// View-Ping. Dieser Hook füllt die Lücke über den BEREITS @Public-fähigen
// Endpoint `POST /public/scrollys/:pk/engagement { sessionId, maxProgress,
// duration }` (trackEngagement → upsert auf (scrollyId, sessionId), dieselbe
// sessionId wie useViewTracking → eine Session-Row).
//
// Strategie: Heartbeat alle 15s (fetch keepalive) + sendBeacon bei pagehide
// (überlebt den Tab-Close, anders als fetch). Duration = akkumulierte
// SICHTBARE Zeit (visibilitychange pausiert) — ein im Hintergrund offener
// Tab zählt nicht als Lesezeit. maxProgress = höchster Renderer-Fortschritt
// aus lib/reader-progress.ts (seit 2026-09-11; vorher window.scrollY, das im
// Container-Scroll des Readers nie stimmte — Scroll-Through war ein Artefakt).

import { useEffect, useRef } from "react";
import { getOrCreateSessionId } from "./useViewTracking";
import {
  readReaderProgress,
  subscribeReaderProgress,
} from "../lib/reader-progress";

const HEARTBEAT_MS = 15_000;

/**
 * Bildschirm des Lesers (28.09.2026): Wie gross sind die Fenster wirklich,
 * und wie breit war die 9:16-Karte? Antwort auf die Kartengroesse-Frage
 * (Benedikt/Jan). Rundung macht das Backend. Beim Seiten-Scroll ist die
 * Karte so breit wie das Fenster, das misst der Rahmen dann mit.
 */
function leserBildschirm(): Record<string, number> {
  try {
    const rahmen = document.querySelector(".scrolly-shell-frame");
    const kartenBreite = rahmen?.getBoundingClientRect().width;
    return {
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
      ...(kartenBreite ? { frameWidth: kartenBreite } : {}),
    };
  } catch {
    return {};
  }
}
const MIN_DURATION_S = 2; // unter 2s = Bounce → nicht senden

interface UseDurationTrackingProps {
  publicKey: string | undefined | null;
  /** Disable für OG-Render / Test-Stubs (wie useViewTracking). */
  disabled?: boolean;
}

export function useDurationTracking({
  publicKey,
  disabled = false,
}: UseDurationTrackingProps): void {
  const sessionIdRef = useRef<string | null>(null);
  const maxProgressRef = useRef(0);
  // Akkumulierte sichtbare Zeit + Zeitpunkt des letzten „visible"-Resume.
  const accumulatedMsRef = useRef(0);
  const resumeTsRef = useRef<number | null>(null);

  useEffect(() => {
    if (disabled || !publicKey) return;
    if (typeof window === "undefined") return;

    sessionIdRef.current ??= getOrCreateSessionId();
    // Neuer publicKey = neue Messung; Stand des Renderers (falls schon
    // gemeldet) als Startwert.
    maxProgressRef.current = readReaderProgress(publicKey);
    // Start der sichtbaren Zeit (falls Tab beim Mount sichtbar ist).
    resumeTsRef.current =
      document.visibilityState === "visible" ? Date.now() : null;

    const currentDurationS = (): number => {
      const live =
        resumeTsRef.current != null ? Date.now() - resumeTsRef.current : 0;
      return Math.round((accumulatedMsRef.current + live) / 1000);
    };

    const payload = () =>
      JSON.stringify({
        sessionId: sessionIdRef.current,
        maxProgress: Number(maxProgressRef.current.toFixed(3)),
        duration: currentDurationS(),
        ...leserBildschirm(),
      });

    const url = `/public/scrollys/${encodeURIComponent(publicKey)}/engagement`;

    const sendHeartbeat = () => {
      if (currentDurationS() < MIN_DURATION_S) return;
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload(),
        keepalive: true,
      }).catch(() => {
        // Fire-and-forget — Tracking-Fehler darf den Reader nicht stören.
      });
    };

    const sendFinal = () => {
      if (currentDurationS() < MIN_DURATION_S) return;
      try {
        // sendBeacon überlebt den Page-Unload; Blob-Type setzt Content-Type.
        navigator.sendBeacon(
          url,
          new Blob([payload()], { type: "application/json" }),
        );
      } catch {
        // Beacon nicht verfügbar → bestes Bemühen, kein Recover.
      }
    };

    const onProgress = (percent: number) => {
      maxProgressRef.current = Math.max(maxProgressRef.current, percent);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        resumeTsRef.current = Date.now();
      } else if (resumeTsRef.current != null) {
        accumulatedMsRef.current += Date.now() - resumeTsRef.current;
        resumeTsRef.current = null;
        sendHeartbeat(); // beim Weg-Tabben den Zwischenstand sichern
      }
    };

    const onPageHide = () => {
      if (resumeTsRef.current != null) {
        accumulatedMsRef.current += Date.now() - resumeTsRef.current;
        resumeTsRef.current = null;
      }
      sendFinal();
    };

    const interval = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
    const unsubscribe = subscribeReaderProgress(publicKey, onProgress);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(interval);
      unsubscribe();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      // Unmount (z.B. Client-Routing zu anderem Scrolly) → Stand sichern.
      onPageHide();
    };
  }, [publicKey, disabled]);
}
